import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Sparkles, Send, X, ArrowRight, FileText, CheckCircle2, Edit3, Download, Loader2,
  User as UserIcon, Users, Calendar, Clock, MapPin, Car, Briefcase, Euro, UserCheck, StickyNote, Plus, Mic, MicOff, Repeat
} from "lucide-react";
import { api, formatApiError, fmtEUR, API_BASE } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

// --- Web Speech API hook (nativo, zero costi, nessuna API key) ---
// iOS-safe: pre-permission via getUserMedia, watchdog timer, preservazione testo esistente,
// gestione corretta di interim/final su onend precoce di iOS/Chrome iOS.
function useSpeechRecognition({ lang = "it-IT", onTranscript, getBaseText }) {
  // stati espliciti: idle | starting | listening | processing
  const [status, setStatus] = useState("idle");
  const [supported, setSupported] = useState(false);
  const recRef = useRef(null);
  const watchdogRef = useRef(null);
  const interimRef = useRef("");
  const finalRef = useRef("");
  const permGrantedRef = useRef(false);
  const baseTextRef = useRef("");

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
    return () => {
      // cleanup su unmount
      try { recRef.current?.stop(); } catch {}
      try { recRef.current?.abort?.(); } catch {}
      if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    };
  }, []);

  const resetWatchdog = useCallback((ms = 15000) => {
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    watchdogRef.current = setTimeout(() => {
      // Timeout di sicurezza: forza stop se il browser non ha chiuso spontaneamente
      try { recRef.current?.stop(); } catch {}
      try { recRef.current?.abort?.(); } catch {}
    }, ms);
  }, []);

  const stop = useCallback(() => {
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    try { recRef.current?.stop(); } catch {}
    // non impostiamo subito a idle: aspettiamo onend per non perdere l'ultimo risultato
    setStatus((s) => (s === "listening" ? "processing" : s));
  }, []);

  // Richiede permesso microfono prima del primo start (necessario su iOS Chrome/Safari)
  const ensurePermission = useCallback(async () => {
    if (permGrantedRef.current) return true;
    if (!navigator.mediaDevices?.getUserMedia) {
      // iOS Chrome vecchie versioni: proviamo lo stesso, SR gestirà da sé
      return true;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // chiudi subito lo stream: SR ne apre uno proprio
      stream.getTracks().forEach((t) => t.stop());
      permGrantedRef.current = true;
      return true;
    } catch (e) {
      const code = e?.name || "";
      if (code === "NotAllowedError" || code === "SecurityError") {
        toast.error("Microfono bloccato. Autorizzalo dalle impostazioni del browser e riprova.");
      } else if (code === "NotFoundError") {
        toast.error("Nessun microfono rilevato sul dispositivo.");
      } else {
        toast.error("Impossibile accedere al microfono.");
      }
      return false;
    }
  }, []);

  const start = useCallback(async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Il tuo browser non supporta il microfono (prova Chrome, Safari o Edge).");
      return;
    }
    // Previene start multipli / sessioni duplicate
    if (recRef.current && (status === "listening" || status === "starting")) return;
    setStatus("starting");
    const ok = await ensurePermission();
    if (!ok) { setStatus("idle"); return; }

    try {
      const rec = new SR();
      rec.lang = lang;
      rec.interimResults = true;
      rec.continuous = false;
      rec.maxAlternatives = 1;

      // snapshot del testo esistente da PRESERVARE (es. "Carica un servizio ")
      const base = (typeof getBaseText === "function" ? getBaseText() : "") || "";
      baseTextRef.current = base.endsWith(" ") || base === "" ? base : base + " ";
      finalRef.current = "";
      interimRef.current = "";

      rec.onstart = () => {
        setStatus("listening");
        resetWatchdog(15000); // iOS a volte non chiude: timeout 15s
      };
      rec.onspeechstart = () => {
        // voce rilevata — ripristina watchdog più lungo
        resetWatchdog(18000);
      };
      rec.onresult = (e) => {
        let interim = "";
        let addedFinal = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) addedFinal += t;
          else interim += t;
        }
        if (addedFinal) finalRef.current += addedFinal;
        interimRef.current = interim;
        onTranscript?.(baseTextRef.current + finalRef.current + interim);
        // reset watchdog ad ogni risultato
        resetWatchdog(15000);
      };
      rec.onnomatch = () => {
        // Nessun match: lascia il testo acquisito, non silenziare
      };
      rec.onerror = (ev) => {
        const err = ev?.error || "unknown";
        if (err === "not-allowed" || err === "service-not-allowed") {
          toast.error("Permesso microfono negato. Abilitalo nelle impostazioni del browser.");
          permGrantedRef.current = false;
        } else if (err === "audio-capture") {
          toast.error("Microfono non disponibile sul dispositivo.");
        } else if (err === "network") {
          toast.error("Rete non disponibile per il riconoscimento vocale. Riprova.");
        } else if (err === "no-speech") {
          // iOS lo emette spesso: non è un vero errore, nessun toast
        } else if (err !== "aborted") {
          toast.error(`Microfono: ${err}`);
        }
        // Non resettiamo subito: aspettiamo onend per preservare parole già riconosciute
      };
      rec.onend = () => {
        if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
        // Se iOS chiude prima senza final, promuovi l'interim rimasto a finale
        if (!finalRef.current && interimRef.current) {
          finalRef.current = interimRef.current;
          interimRef.current = "";
          onTranscript?.(baseTextRef.current + finalRef.current);
        }
        // Avviso se non è stata rilevata alcuna voce
        if (!finalRef.current && !interimRef.current && baseTextRef.current === "") {
          toast.message("Non ho rilevato alcuna voce. Riprova.");
        }
        setStatus("idle");
        recRef.current = null;
      };

      recRef.current = rec;
      // Piccolo delay per iOS (lasciare che getUserMedia liberi lo stream)
      setTimeout(() => {
        try { rec.start(); }
        catch (e) {
          // InvalidStateError se già in start — abort e riprova
          try { rec.abort?.(); } catch {}
          setStatus("idle");
          toast.error("Impossibile avviare il microfono. Riprova.");
        }
      }, 50);
    } catch (e) {
      setStatus("idle");
      toast.error("Impossibile inizializzare il microfono.");
    }
  }, [lang, onTranscript, getBaseText, ensurePermission, resetWatchdog, status]);

  return {
    status,
    supported,
    start,
    stop,
    listening: status === "listening",
    starting: status === "starting",
    processing: status === "processing",
  };
}

const SUGGESTIONS = [
  "Quanto ho incassato questo mese?",
  "Carica un nuovo servizio per Riccardo Gentili domani alle 9 da Via Cassia 901 al Colosseo, 3 pax, passeggero Rossella, 150 euro",
  "Fammi un preventivo per Mario Rossi da Fiumicino al Colosseo domani alle 10, 2 pax, 180 euro",
  "Quanti servizi ho domani?",
  "Quanto ho fatto con Uber questa settimana?",
  "Fammi vedere i servizi senza driver.",
];

const SECTION_LINKS = [
  { k: "preventiv", to: "/preventivi", label: "Vedi Preventivi" },
  { k: "prenotazion", to: "/prenotazioni", label: "Vedi Prenotazioni" },
  { k: "uber", to: "/uber", label: "Vedi Uber" },
  { k: "fornitor", to: "/scambio-servizi", label: "Vedi Scambio Servizi" },
  { k: "conteggi", to: "/conteggi", label: "Vedi Conteggi" },
  { k: "incass", to: "/incassi", label: "Vedi Incassi" },
];

function detectLink(text) {
  const t = (text || "").toLowerCase();
  return SECTION_LINKS.find((l) => t.includes(l.k));
}

// Estrae blocchi strutturati dal testo del messaggio AI
function parseStructuredBlocks(text) {
  if (!text) return { clean: "", preview: null, created: null, bookingPreview: null, bookingCreated: null, bookingUpdated: null, exchangePreview: null, exchangeCreated: null };
  let clean = text;
  const grab = (re) => {
    const m = clean.match(re);
    if (!m) return null;
    try {
      const data = JSON.parse(m[1].trim());
      clean = clean.replace(re, "").trim();
      return data;
    } catch {
      clean = clean.replace(re, "").trim();
      return null;
    }
  };
  const preview = grab(/\[\[QUOTE_PREVIEW\]\]([\s\S]*?)\[\[\/QUOTE_PREVIEW\]\]/);
  const created = grab(/\[\[QUOTE_CREATED\]\]([\s\S]*?)\[\[\/QUOTE_CREATED\]\]/);
  const bookingPreview = grab(/\[\[BOOKING_PREVIEW\]\]([\s\S]*?)\[\[\/BOOKING_PREVIEW\]\]/);
  const bookingCreated = grab(/\[\[BOOKING_CREATED\]\]([\s\S]*?)\[\[\/BOOKING_CREATED\]\]/);
  const bookingUpdated = grab(/\[\[BOOKING_UPDATED\]\]([\s\S]*?)\[\[\/BOOKING_UPDATED\]\]/);
  const exchangePreview = grab(/\[\[EXCHANGE_PREVIEW\]\]([\s\S]*?)\[\[\/EXCHANGE_PREVIEW\]\]/);
  const exchangeCreated = grab(/\[\[EXCHANGE_CREATED\]\]([\s\S]*?)\[\[\/EXCHANGE_CREATED\]\]/);
  return { clean, preview, created, bookingPreview, bookingCreated, bookingUpdated, exchangePreview, exchangeCreated };
}

// Formattazione data italiana da ISO YYYY-MM-DD
function fmtDateIT(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  if (!y) return iso;
  const months = ["gennaio","febbraio","marzo","aprile","maggio","giugno","luglio","agosto","settembre","ottobre","novembre","dicembre"];
  const mi = parseInt(m, 10) - 1;
  return `${parseInt(d, 10)} ${months[mi] || m} ${y}`;
}

// ------------------- CARD PREVIEW -------------------
function QuotePreviewCard({ draft, onConfirm, onEdit, submitting }) {
  const total = draft.total ?? 0;
  const subtotal = draft.subtotal ?? 0;
  const vat = draft.vat_amount ?? 0;
  const vatRate = draft.vat_rate ?? 10;
  const custLabel = draft.customer_match?.name || draft.customer_name_input || "—";
  const isNewCust = !!draft.customer_new;
  return (
    <div className="ai-quote-card" data-testid="assistant-quote-preview">
      <div className="ai-quote-card-head">
        <FileText size={14} />
        <span>CREA PREVENTIVO</span>
      </div>
      <div className="ai-quote-grid">
        <div className="ai-quote-row">
          <span className="ai-quote-k">Cliente</span>
          <span className="ai-quote-v">
            {custLabel}
            {isNewCust && <span className="ai-quote-tag">NUOVO</span>}
          </span>
        </div>
        {draft.passenger_name && (
          <div className="ai-quote-row">
            <span className="ai-quote-k">Passeggero</span>
            <span className="ai-quote-v">{draft.passenger_name}</span>
          </div>
        )}
        <div className="ai-quote-row">
          <span className="ai-quote-k">Data</span>
          <span className="ai-quote-v">{fmtDateIT(draft.date)}{draft.time ? ` · ${draft.time}` : ""}</span>
        </div>
        <div className="ai-quote-row">
          <span className="ai-quote-k">Percorso</span>
          <span className="ai-quote-v" style={{ whiteSpace: "normal" }}>
            {draft.pickup} <ArrowRight size={11} style={{ display: "inline", color: "#D4A72C", margin: "0 4px" }} /> {draft.destination}
          </span>
        </div>
        <div className="ai-quote-row">
          <span className="ai-quote-k">Pax</span>
          <span className="ai-quote-v">{draft.passengers}{draft.luggage ? ` · ${draft.luggage} bagagli` : ""}</span>
        </div>
        {draft.vehicle && (
          <div className="ai-quote-row">
            <span className="ai-quote-k">Veicolo</span>
            <span className="ai-quote-v">{draft.vehicle}</span>
          </div>
        )}
      </div>
      <div className="ai-quote-totals">
        <div className="ai-quote-trow">
          <span>Imponibile</span><span>{fmtEUR(subtotal)}</span>
        </div>
        <div className="ai-quote-trow">
          <span>IVA {vatRate}%</span><span>{fmtEUR(vat)}</span>
        </div>
        <div className="ai-quote-trow ai-quote-trow-total">
          <span>TOTALE</span><span>{fmtEUR(total)}</span>
        </div>
      </div>
      <div className="ai-quote-actions">
        <button
          onClick={onEdit}
          className="ai-quote-btn-sec"
          data-testid="assistant-quote-edit"
          disabled={submitting}
        >
          <Edit3 size={13} /> MODIFICA
        </button>
        <button
          onClick={onConfirm}
          className="ai-quote-btn-pri"
          data-testid="assistant-quote-confirm"
          disabled={submitting}
        >
          {submitting ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />}
          {submitting ? "CREAZIONE…" : "CONFERMA E CREA"}
        </button>
      </div>
    </div>
  );
}

// ============== BOOKING PREVIEW CARD (icon-prefix rows) ==============
function RowIcon({ Icon, label, value, placeholder = "Non specificato" }) {
  return (
    <div className="ai-brow">
      <span className="ai-brow-ic"><Icon size={13} strokeWidth={2.4} /></span>
      <span className="ai-brow-k">{label}</span>
      <span className={`ai-brow-v ${!value ? "ai-brow-v-empty" : ""}`}>
        {value || placeholder}
      </span>
    </div>
  );
}

function BookingPreviewCard({ draft, onConfirm, onEdit, submitting }) {
  const custLabel = draft.customer_match?.name || draft.customer_name_input || "";
  const isNewCust = !!draft.customer_new;
  const vehLabel = draft.vehicle_match
    ? `${draft.vehicle_match.brand || ""} ${draft.vehicle_match.model || ""}`.trim()
    : (draft.vehicle_input || "");
  const drvLabel = draft.driver_match?.name || draft.driver_input || "";
  const supLabel = draft.supplier_match?.name || draft.supplier_input || "";
  const dateLabel = draft.date ? fmtDateIT(draft.date) : "";
  return (
    <div className="ai-quote-card" data-testid="assistant-booking-preview">
      <div className="ai-quote-card-head">
        <Calendar size={14} />
        <span>NUOVO SERVIZIO</span>
      </div>
      <div className="ai-brows">
        <RowIcon Icon={UserIcon} label="Cliente" value={
          custLabel ? (
            <>{custLabel}{isNewCust && <span className="ai-quote-tag">NUOVO</span>}</>
          ) : ""
        } />
        <RowIcon Icon={Users} label="Passeggero" value={draft.passenger_name || ""} />
        <RowIcon Icon={Calendar} label="Data" value={dateLabel} />
        <RowIcon Icon={Clock} label="Ora" value={draft.time || ""} />
        <RowIcon Icon={MapPin} label="Partenza" value={draft.pickup || ""} />
        <RowIcon Icon={MapPin} label="Destinazione" value={draft.destination || ""} />
        <RowIcon Icon={Users} label="Passeggeri" value={draft.passengers ? String(draft.passengers) : ""} />
        <RowIcon Icon={Briefcase} label="Bagagli" value={draft.luggage ? String(draft.luggage) : ""} />
        <RowIcon Icon={Car} label="Veicolo" value={vehLabel} />
        <RowIcon Icon={UserCheck} label="Autista" value={drvLabel || "Non assegnato"} />
        {supLabel && <RowIcon Icon={UserIcon} label="Fornitore" value={supLabel} />}
        <RowIcon Icon={Euro} label="Prezzo servizio" value={draft.price ? fmtEUR(draft.price) : ""} />
        {draft.notes && <RowIcon Icon={StickyNote} label="Note" value={draft.notes} />}
      </div>
      <div className="ai-quote-actions">
        <button
          onClick={onEdit}
          className="ai-quote-btn-sec"
          data-testid="assistant-booking-edit"
          disabled={submitting}
        >
          <Edit3 size={13} /> MODIFICA
        </button>
        <button
          onClick={onConfirm}
          className="ai-quote-btn-pri"
          data-testid="assistant-booking-confirm"
          disabled={submitting}
        >
          {submitting ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />}
          {submitting ? "CREAZIONE…" : "CONFERMA E CARICA SERVIZIO"}
        </button>
      </div>
    </div>
  );
}

// ============== BOOKING "CREATION IN PROGRESS" (checklist animata) ==============
function BookingCreating() {
  const steps = [
    "Validazione dati",
    "Creazione nuovo record",
    "Salvataggio nel database",
    "Assegnazione ID servizio",
    "Verifica e completamento",
  ];
  return (
    <div className="ai-create-steps" data-testid="assistant-booking-creating">
      <div className="ai-create-title">Creazione servizio in corso…</div>
      {steps.map((s, i) => (
        <div key={i} className="ai-create-step" style={{ animationDelay: `${i * 160}ms` }}>
          <span className="ai-create-dot"><CheckCircle2 size={13} /></span>
          <span>{s}</span>
        </div>
      ))}
    </div>
  );
}

// ============== BOOKING CREATED CARD ==============
function BookingCreatedCard({ data, onOpen, onCreateAnother }) {
  return (
    <div className="ai-quote-card ai-quote-card-success" data-testid="assistant-booking-created">
      <div className="ai-quote-card-head ai-quote-card-head-success">
        <CheckCircle2 size={14} />
        <span>SERVIZIO CREATO</span>
        {data.booking_number && (
          <span className="ai-booking-num-inline">#{data.booking_number}</span>
        )}
      </div>
      <div className="ai-brows">
        <RowIcon Icon={UserIcon} label="Cliente" value={data.customer_name || ""} />
        <RowIcon Icon={Users} label="Passeggero" value={data.passenger_name || ""} />
        <RowIcon Icon={Calendar} label="Data / Ora" value={`${fmtDateIT(data.date)}${data.time ? ` · ${data.time}` : ""}`} />
        <RowIcon Icon={MapPin} label="Percorso" value={
          data.pickup && data.destination
            ? <span>{data.pickup}<ArrowRight size={11} style={{ display: "inline", color: "#D4A72C", margin: "0 4px" }} />{data.destination}</span>
            : ""
        } />
        <RowIcon Icon={Users} label="Passeggeri" value={data.passengers ? String(data.passengers) : ""} />
        {data.vehicle_label && <RowIcon Icon={Car} label="Veicolo" value={data.vehicle_label} />}
        <RowIcon Icon={UserCheck} label="Autista" value={data.driver_label || "Non assegnato"} />
        <RowIcon Icon={Euro} label="Prezzo servizio" value={data.price ? fmtEUR(data.price) : ""} />
      </div>
      <div className="ai-quote-actions">
        <button
          onClick={() => onOpen(data.booking_id)}
          className="ai-quote-btn-sec"
          data-testid="assistant-booking-open"
        >
          <FileText size={13} /> APRI SERVIZIO
        </button>
        <button
          onClick={() => onOpen(data.booking_id, true)}
          className="ai-quote-btn-pri"
          data-testid="assistant-booking-edit-created"
        >
          <Edit3 size={13} /> MODIFICA
        </button>
      </div>
      <button
        onClick={onCreateAnother}
        className="ai-quote-btn-new"
        data-testid="assistant-booking-create-another"
      >
        <Plus size={14} /> CREA ALTRO SERVIZIO
      </button>
    </div>
  );
}

// ============== BOOKING UPDATED (driver assigned) ==============
function BookingUpdatedCard({ data, onOpen }) {
  const route = data.pickup && data.destination
    ? <span>{data.pickup}<ArrowRight size={11} style={{ display: "inline", color: "#D4A72C", margin: "0 4px" }} />{data.destination}</span>
    : "";
  return (
    <div className="ai-quote-card ai-quote-card-success" data-testid="assistant-booking-updated">
      <div className="ai-quote-card-head ai-quote-card-head-success">
        <UserCheck size={14} />
        <span>AUTISTA ASSEGNATO</span>
        {data.booking_number && <span className="ai-booking-num-inline">#{data.booking_number}</span>}
      </div>
      <div className="ai-brows">
        <RowIcon Icon={UserCheck} label="Autista" value={data.driver_label || ""} />
        {data.driver_phone && <RowIcon Icon={UserIcon} label="Telefono" value={data.driver_phone} />}
        {data.passenger_name && <RowIcon Icon={Users} label="Passeggero" value={data.passenger_name} />}
        {route && <RowIcon Icon={MapPin} label="Percorso" value={route} />}
        {data.date && <RowIcon Icon={Calendar} label="Data / Ora" value={`${fmtDateIT(data.date)}${data.time ? ` · ${data.time}` : ""}`} />}
        {data.driver_price > 0 && <RowIcon Icon={Euro} label="Base compenso" value={fmtEUR(data.driver_price)} />}
        {data.driver_cost > 0 && <RowIcon Icon={Euro} label="Compenso autista" value={fmtEUR(data.driver_cost)} />}
      </div>
      <div className="ai-quote-actions">
        <button
          onClick={() => onOpen(data.booking_id)}
          className="ai-quote-btn-sec"
          data-testid="assistant-booking-updated-open"
        >
          <FileText size={13} /> APRI SERVIZIO
        </button>
        <button
          onClick={() => onOpen(data.booking_id, true)}
          className="ai-quote-btn-pri"
          data-testid="assistant-booking-updated-edit"
        >
          <Edit3 size={13} /> MODIFICA
        </button>
      </div>
    </div>
  );
}

// ============== EXCHANGE PREVIEW CARD ==============
function ExchangePreviewCard({ draft, onConfirm, onEdit, submitting }) {
  const b = draft.booking || {};
  const col = draft.colleague || {};
  return (
    <div className="ai-quote-card" data-testid="assistant-exchange-preview">
      <div className="ai-quote-card-head">
        <Repeat size={14} />
        <span>SCAMBIO SERVIZI</span>
      </div>
      <div className="ai-brows">
        <RowIcon Icon={UserIcon} label="Collega" value={col.name || ""} />
        <RowIcon Icon={FileText} label="Servizio" value={b.booking_number || ""} />
        {b.customer_name && <RowIcon Icon={UserIcon} label="Cliente" value={b.customer_name} />}
        {b.passenger_name && <RowIcon Icon={Users} label="Passeggero" value={b.passenger_name} />}
        <RowIcon Icon={Calendar} label="Data / Ora" value={`${fmtDateIT(b.date)}${b.time ? ` · ${b.time}` : ""}`} />
        <RowIcon Icon={MapPin} label="Percorso" value={
          b.pickup && b.destination
            ? <span>{b.pickup}<ArrowRight size={11} style={{ display: "inline", color: "#D4A72C", margin: "0 4px" }} />{b.destination}</span>
            : ""
        } />
        <RowIcon Icon={Users} label="Pax" value={b.passengers ? String(b.passengers) : ""} />
        {draft.notes && <RowIcon Icon={StickyNote} label="Note" value={draft.notes} />}
      </div>
      <div className="ai-quote-totals">
        <div className="ai-quote-trow"><span>Prezzo servizio</span><span>{fmtEUR(draft.price)}</span></div>
        <div className="ai-quote-trow"><span>Costo riconosciuto</span><span>{fmtEUR(draft.cost)}</span></div>
        <div className="ai-quote-trow ai-quote-trow-total"><span>MARGINE</span><span>{fmtEUR(draft.margin)}</span></div>
      </div>
      <div className="ai-quote-actions">
        <button
          onClick={onEdit}
          className="ai-quote-btn-sec"
          data-testid="assistant-exchange-edit"
          disabled={submitting}
        >
          <Edit3 size={13} /> MODIFICA
        </button>
        <button
          onClick={onConfirm}
          className="ai-quote-btn-pri"
          data-testid="assistant-exchange-confirm"
          disabled={submitting}
        >
          {submitting ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />}
          {submitting ? "INVIO…" : "CONFERMA E INVIA"}
        </button>
      </div>
    </div>
  );
}

// ============== EXCHANGE CREATED CARD ==============
function ExchangeCreatedCard({ data, onOpen }) {
  return (
    <div className="ai-quote-card ai-quote-card-success" data-testid="assistant-exchange-created">
      <div className="ai-quote-card-head ai-quote-card-head-success">
        <CheckCircle2 size={14} />
        <span>SCAMBIO INVIATO</span>
        {data.exchange_number && <span className="ai-booking-num-inline">#{data.exchange_number}</span>}
      </div>
      <div className="ai-brows">
        <RowIcon Icon={UserIcon} label="Collega" value={data.colleague_name || ""} />
        <RowIcon Icon={FileText} label="Servizio" value={data.booking_number || ""} />
        {data.passenger_name && <RowIcon Icon={Users} label="Passeggero" value={data.passenger_name} />}
        <RowIcon Icon={Calendar} label="Data / Ora" value={`${fmtDateIT(data.date)}${data.time ? ` · ${data.time}` : ""}`} />
        <RowIcon Icon={MapPin} label="Percorso" value={
          data.pickup && data.destination
            ? <span>{data.pickup}<ArrowRight size={11} style={{ display: "inline", color: "#D4A72C", margin: "0 4px" }} />{data.destination}</span>
            : ""
        } />
        <RowIcon Icon={Euro} label="Costo" value={fmtEUR(data.cost)} />
        <RowIcon Icon={Euro} label="Margine" value={fmtEUR(data.margin)} />
      </div>
      <div className="ai-quote-actions">
        <button
          onClick={onOpen}
          className="ai-quote-btn-pri"
          data-testid="assistant-exchange-open"
          style={{ gridColumn: "1 / -1" }}
        >
          <Repeat size={13} /> VEDI SCAMBIO
        </button>
      </div>
    </div>
  );
}

// ------------------- CARD CREATED (QUOTE) -------------------
function QuoteCreatedCard({ data, onOpen }) {
  const total = data.total ?? 0;
  const pdfUrl = data.pdf_url ? `${API_BASE.replace(/\/api$/, "")}${data.pdf_url}` : null;
  return (
    <div className="ai-quote-card ai-quote-card-success" data-testid="assistant-quote-created">
      <div className="ai-quote-card-head ai-quote-card-head-success">
        <CheckCircle2 size={14} />
        <span>PREVENTIVO CREATO</span>
      </div>
      <div className="ai-quote-success-number">{data.quote_number || "—"}</div>
      <div className="ai-quote-grid">
        <div className="ai-quote-row">
          <span className="ai-quote-k">Cliente</span>
          <span className="ai-quote-v">{data.customer_name || "—"}</span>
        </div>
        {data.passenger_name && (
          <div className="ai-quote-row">
            <span className="ai-quote-k">Passeggero</span>
            <span className="ai-quote-v">{data.passenger_name}</span>
          </div>
        )}
        <div className="ai-quote-row">
          <span className="ai-quote-k">Percorso</span>
          <span className="ai-quote-v" style={{ whiteSpace: "normal" }}>
            {data.pickup} <ArrowRight size={11} style={{ display: "inline", color: "#D4A72C", margin: "0 4px" }} /> {data.destination}
          </span>
        </div>
        <div className="ai-quote-row">
          <span className="ai-quote-k">Data</span>
          <span className="ai-quote-v">{fmtDateIT(data.date)}{data.time ? ` · ${data.time}` : ""}</span>
        </div>
        <div className="ai-quote-row">
          <span className="ai-quote-k">Pax</span>
          <span className="ai-quote-v">{data.passengers}</span>
        </div>
      </div>
      <div className="ai-quote-totals">
        <div className="ai-quote-trow ai-quote-trow-total">
          <span>TOTALE</span><span>{fmtEUR(total)}</span>
        </div>
      </div>
      <div className="ai-quote-actions">
        <button
          onClick={() => onOpen(data.quote_id)}
          className="ai-quote-btn-sec"
          data-testid="assistant-quote-open"
        >
          <FileText size={13} /> APRI PREVENTIVO
        </button>
        {pdfUrl && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="ai-quote-btn-pri"
            data-testid="assistant-quote-pdf"
          >
            <Download size={13} /> SCARICA PDF
          </a>
        )}
      </div>
    </div>
  );
}

export default function AssistantBar() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [executing, setExecuting] = useState(false);
  const { user } = useAuth();
  const storeKey = `striano-ai-${user?.id || "anon"}`;
  const newSid = () => "assist-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
  const [messages, setMessages] = useState([]); // {role, text, draft?, created?}
  const [sessionId, setSessionId] = useState(newSid);
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(storeKey) || "null");
      if (s?.sessionId) { setSessionId(s.sessionId); setMessages(s.messages || []); }
    } catch {}
    setRestored(true);
  }, [storeKey]);
  useEffect(() => {
    if (!restored) return;
    try { localStorage.setItem(storeKey, JSON.stringify({ sessionId, messages: messages.slice(-40) })); } catch {}
  }, [restored, storeKey, sessionId, messages]);
  const resetConversation = async () => {
    try { await api.post("/ai/session/reset", { session_id: sessionId }); } catch {}
    setMessages([]);
    setSessionId(newSid());
    toast.success("Nuova conversazione avviata");
  };
  const nav = useNavigate();
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setTimeout(() => inputRef.current?.focus(), 320);
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/ai/assistant", { question, session_id: sessionId });
      if (r.data?.session_id && r.data.session_id !== sessionId) setSessionId(r.data.session_id);
      const raw = r.data?.answer || "—";
      const parsed = parseStructuredBlocks(raw);
      setMessages((m) => [...m, {
        role: "assistant",
        text: parsed.clean || raw,
        draft: parsed.preview,
        created: parsed.created,
        bookingDraft: parsed.bookingPreview,
        bookingCreated: parsed.bookingCreated,
        bookingUpdated: parsed.bookingUpdated,
        exchangeDraft: parsed.exchangePreview,
        exchangeCreated: parsed.exchangeCreated,
      }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: `Errore: ${formatApiError(e)}` }]);
    } finally {
      setSending(false);
    }
  };

  const confirmQuote = async (draft, msgIndex) => {
    setExecuting(true);
    try {
      const r = await api.post("/ai/execute-quote", { draft, session_id: sessionId });
      const data = r.data || {};
      setMessages((m) => {
        const out = [...m];
        if (out[msgIndex]) out[msgIndex] = { ...out[msgIndex], draft: null, consumed: true };
        out.push({ role: "assistant", text: data.already_created ? `Preventivo ${data.quote_number || ""} già creato: nessun duplicato.` : `Preventivo ${data.quote_number || ""} creato correttamente.`, created: data });
        return out;
      });
      toast.success(`Preventivo ${data.quote_number || ""} creato`);
    } catch (e) {
      toast.error(formatApiError(e));
      setMessages((m) => [...m, { role: "assistant", text: `Errore nella creazione: ${formatApiError(e)}` }]);
    } finally {
      setExecuting(false);
    }
  };

  const confirmBooking = async (draft, msgIndex) => {
    setExecuting(true);
    setMessages((m) => {
      const out = [...m];
      if (out[msgIndex]) out[msgIndex] = { ...out[msgIndex], bookingDraft: null, creatingBooking: true };
      return out;
    });
    try {
      const r = await api.post("/ai/execute-booking", { draft, session_id: sessionId });
      const data = r.data || {};
      setMessages((m) => {
        const out = [...m];
        if (out[msgIndex]) out[msgIndex] = { ...out[msgIndex], creatingBooking: false, consumed: true };
        out.push({
          role: "assistant",
          text: data.already_created
            ? `Servizio ${data.booking_number || ""} già creato: nessun duplicato.`
            : `Servizio ${data.booking_number || ""} salvato e verificato nel gestionale.`,
          bookingCreated: data,
        });
        return out;
      });
      toast.success(`Servizio ${data.booking_number || ""} creato`);
    } catch (e) {
      setMessages((m) => {
        const out = [...m];
        if (out[msgIndex]) out[msgIndex] = { ...out[msgIndex], creatingBooking: false, bookingDraft: draft };
        return out;
      });
      const msg = formatApiError(e);
      toast.error(msg);
      setMessages((m) => [...m, { role: "assistant", text: `Non sono riuscito a creare il servizio: ${msg}. Nessun dato è stato salvato.` }]);
    } finally {
      setExecuting(false);
    }
  };

  const editDraft = (draft) => {
    const cust = draft.customer_match?.name || draft.customer_name_input || "cliente";
    setInput(`Modifica il preventivo per ${cust}: `);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const editBookingDraft = (draft) => {
    const cust = draft.customer_match?.name || draft.customer_name_input || "servizio";
    setInput(`Modifica il servizio di ${cust}: `);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const openCreatedQuote = (id) => {
    setOpen(false);
    nav(`/preventivi?open=${id}`);
  };

  const openCreatedBooking = (id, edit = false) => {
    setOpen(false);
    nav(`/prenotazioni?open=${id}${edit ? "&edit=1" : ""}`);
  };

  const createAnotherBooking = () => {
    setInput("Carica un nuovo servizio ");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const confirmExchange = async (draft, msgIndex) => {
    setExecuting(true);
    try {
      const r = await api.post("/ai/execute-exchange", { draft, session_id: sessionId });
      const data = r.data || {};
      setMessages((m) => {
        const out = [...m];
        if (out[msgIndex]) out[msgIndex] = { ...out[msgIndex], exchangeDraft: null, consumed: true };
        out.push({
          role: "assistant",
          text: `Scambio ${data.exchange_number || ""} inviato a ${data.colleague_name || ""}.`,
          exchangeCreated: data,
        });
        return out;
      });
      toast.success(`Scambio ${data.exchange_number || ""} inviato`);
    } catch (e) {
      const msg = formatApiError(e);
      toast.error(msg);
      setMessages((m) => [...m, { role: "assistant", text: `Errore nell'invio dello scambio: ${msg}` }]);
    } finally {
      setExecuting(false);
    }
  };

  const editExchange = (draft) => {
    const col = draft.colleague?.name || "collega";
    setInput(`Modifica lo scambio con ${col}: `);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const openCreatedExchange = () => {
    setOpen(false);
    nav("/scambio-servizi");
  };

  // Voice input (Italian STT via native Web Speech API, iOS-safe)
  const inputRef_forMic = inputRef; // alias per chiarezza
  const { listening, starting, processing, supported: micSupported, start: micStart, stop: micStop } = useSpeechRecognition({
    lang: "it-IT",
    onTranscript: (text) => setInput(text),
    // preserva eventuale testo già scritto dall'utente nell'input
    getBaseText: () => (inputRef_forMic.current?.value || ""),
  });
  const toggleMic = () => {
    if (listening || starting) {
      micStop();
    } else if (!processing) {
      // NON svuotiamo l'input: preserviamo il testo esistente
      micStart();
    }
  };

  return (
    <>
      {/* ============ FLOATING LIQUID GOLD ORB ============ */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ai-fab"
        data-testid="assistant-fab"
        aria-label="Apri Assistente Striano"
      >
        <span className="ai-fab-halo" />
        <span className="ai-fab-ring" />
        <span className="ai-fab-ring r2" />
        <span className="ai-fab-orb">
          <Sparkles size={22} strokeWidth={2.25} className="ai-fab-icon" />
        </span>
        <span className="ai-fab-tip">Assistente Striano</span>
      </button>

      {/* ============ SLIDE-OVER PANEL · DARK GLASS / BLACK MARBLE ============ */}
      {open && (
        <>
          <div
            className="ai-backdrop"
            onClick={() => setOpen(false)}
            data-testid="assistant-backdrop"
          />
          <aside className="ai-panel" data-testid="assistant-panel" role="dialog" aria-modal="true">
            <div className="ai-panel-header">
              <div className="flex items-center gap-3">
                <div className="ai-mini-orb">
                  <Sparkles size={18} strokeWidth={2.3} />
                </div>
                <div>
                  <div className="font-display text-[20px] font-bold leading-tight" style={{ color: "#F5F2E8" }}>
                    Assistente <span className="text-gold-metal">Striano</span>
                  </div>
                  <div className="text-[10px] font-mono-num uppercase tracking-[0.22em]" style={{ color: "#B8A270" }}>
                    <span className="ai-live-dot" /> AI AGENT · ONLINE
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
              <button
                onClick={resetConversation}
                data-testid="assistant-new-conversation"
                title="Nuova conversazione"
                aria-label="Nuova conversazione"
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                style={{ background: "linear-gradient(145deg, rgba(14,13,10,0.6), rgba(6,5,4,0.8))", border: "1px solid rgba(214,166,42,0.22)", color: "#B8A270" }}
              >
                <Plus size={18} />
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="assistant-close"
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-all"
                style={{
                  background: "linear-gradient(145deg, rgba(14,13,10,0.6), rgba(6,5,4,0.8))",
                  border: "1px solid rgba(214,166,42,0.22)",
                  color: "#B8A270",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "#FFE08A"; e.currentTarget.style.borderColor = "rgba(242,200,91,0.55)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "#B8A270"; e.currentTarget.style.borderColor = "rgba(214,166,42,0.22)"; }}
                aria-label="Chiudi"
              >
                <X size={18} />
              </button>
              </div>
            </div>

            <div ref={scrollRef} className="ai-panel-body flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {messages.length === 0 && (
                <div className="space-y-4 luxe-fade-in">
                  <div className="text-center py-2">
                    <div className="font-display text-[22px] font-bold" style={{ color: "#F5F2E8" }}>
                      Come posso <span className="text-gold-metal">aiutarti</span>?
                    </div>
                    <p className="text-[12px] mt-1" style={{ color: "#9A9183" }}>
                      Chiedimi dati o <span style={{ color: "#F2C85B" }}>crea direttamente un preventivo</span> con una frase naturale.
                    </p>
                    <div className="gold-divider mt-4 mx-10" />
                  </div>

                  <div className="text-[10px] font-mono-num uppercase tracking-[0.22em] pt-1" style={{ color: "#8A7E68" }}>
                    Suggerimenti
                  </div>
                  <div className="space-y-2.5">
                    {SUGGESTIONS.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => ask(s)}
                        data-testid={`assistant-suggestion-${i}`}
                        className="ai-suggestion"
                      >
                        <span className="inline-flex items-center gap-2">
                          <ArrowRight size={13} style={{ color: "#D4A72C" }} />
                          {s}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => {
                const link = m.role === "assistant" && !m.draft && !m.created && !m.bookingDraft && !m.bookingCreated && !m.bookingUpdated && !m.exchangeDraft && !m.exchangeCreated && !m.creatingBooking ? detectLink(m.text) : null;
                const isUser = m.role === "user";
                return (
                  <div key={i} className={`flex luxe-fade-in ${isUser ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[92%] px-4 py-3 text-[13.5px] leading-relaxed whitespace-pre-wrap ${isUser ? "ai-bubble-user" : "ai-bubble-ai"}`}>
                      {m.text && <div>{m.text}</div>}
                      {m.draft && (
                        <QuotePreviewCard
                          draft={m.draft}
                          submitting={executing}
                          onConfirm={() => confirmQuote(m.draft, i)}
                          onEdit={() => editDraft(m.draft)}
                        />
                      )}
                      {m.created && (
                        <QuoteCreatedCard
                          data={m.created}
                          onOpen={openCreatedQuote}
                        />
                      )}
                      {m.bookingDraft && (
                        <BookingPreviewCard
                          draft={m.bookingDraft}
                          submitting={executing}
                          onConfirm={() => confirmBooking(m.bookingDraft, i)}
                          onEdit={() => editBookingDraft(m.bookingDraft)}
                        />
                      )}
                      {m.creatingBooking && <BookingCreating />}
                      {m.bookingCreated && (
                        <BookingCreatedCard
                          data={m.bookingCreated}
                          onOpen={openCreatedBooking}
                          onCreateAnother={createAnotherBooking}
                        />
                      )}
                      {m.bookingUpdated && (
                        <BookingUpdatedCard
                          data={m.bookingUpdated}
                          onOpen={openCreatedBooking}
                        />
                      )}
                      {m.exchangeDraft && (
                        <ExchangePreviewCard
                          draft={m.exchangeDraft}
                          submitting={executing}
                          onConfirm={() => confirmExchange(m.exchangeDraft, i)}
                          onEdit={() => editExchange(m.exchangeDraft)}
                        />
                      )}
                      {m.exchangeCreated && (
                        <ExchangeCreatedCard
                          data={m.exchangeCreated}
                          onOpen={openCreatedExchange}
                        />
                      )}
                      {link && (
                        <div>
                          <button
                            onClick={() => { setOpen(false); nav(link.to); }}
                            data-testid={`assistant-nav-${link.to.replace("/", "")}`}
                            className="ai-link-chip"
                          >
                            {link.label} <ArrowRight size={11} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {sending && (
                <div className="flex justify-start luxe-fade-in">
                  <div className="ai-bubble-ai px-4 py-3">
                    <div className="ai-typing">
                      <span /><span /><span />
                    </div>
                    <div className="text-[10px] font-mono-num uppercase tracking-[0.22em] mt-2" style={{ color: "#8A7E68" }}>
                      sto consultando il gestionale…
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              className="px-5 py-4"
              style={{
                borderTop: "1px solid rgba(214,166,42,0.18)",
                background: "linear-gradient(180deg, rgba(6,5,4,0.4) 0%, rgba(4,4,4,0.9) 100%)",
              }}
            >
              <div className="flex items-center gap-2.5">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (!sending) ask();
                    }
                  }}
                  placeholder={
                    listening ? "Sto ascoltando…" :
                    starting ? "Avvio microfono…" :
                    processing ? "Elaborazione…" :
                    "Scrivi la tua domanda o parla al microfono…"
                  }
                  disabled={sending}
                  data-testid="assistant-panel-input"
                  className="ai-input"
                />
                {micSupported && (
                  <button
                    type="button"
                    onClick={toggleMic}
                    disabled={sending || processing}
                    data-testid="assistant-mic-btn"
                    data-mic-state={listening ? "listening" : (starting ? "starting" : (processing ? "processing" : "idle"))}
                    aria-label={listening ? "Stop registrazione" : (starting ? "Avvio microfono" : (processing ? "Elaborazione" : "Avvia registrazione vocale"))}
                    className={`ai-mic-btn ${listening ? "ai-mic-btn-live" : ""} ${starting ? "ai-mic-btn-starting" : ""} ${processing ? "ai-mic-btn-processing" : ""}`}
                    title={listening ? "Clicca per fermare" : (starting ? "Avvio…" : (processing ? "Elaborazione…" : "Parla in italiano"))}
                  >
                    {starting || processing
                      ? <Loader2 size={17} strokeWidth={2.3} className="spin" />
                      : (listening ? <MicOff size={17} strokeWidth={2.3} /> : <Mic size={17} strokeWidth={2.3} />)}
                    {listening && <span className="ai-mic-pulse" />}
                  </button>
                )}
                <button
                  onClick={() => ask()}
                  disabled={sending || !input.trim()}
                  data-testid="assistant-panel-send"
                  className="ai-send-btn"
                  aria-label="Invia"
                >
                  <Send size={17} strokeWidth={2.3} />
                </button>
              </div>
              <div
                className="text-[9.5px] font-mono-num uppercase tracking-[0.22em] text-center mt-3"
                style={{ color: "#6E6553" }}
              >
                STRIANO NCC · AI AGENT · Powered by Emergent
              </div>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
